import { supabase } from './supabaseClient'
import type { QuizCategory, QuizDifficulty, QuizQuestion, QuizSession } from '../types'

export async function generateQuiz(
  positionTitle: string,
  techStack: string[],
  difficulty: QuizDifficulty,
  questionCount: number,
  categories: QuizCategory[]
): Promise<QuizQuestion[]> {
  const { data: { user }, error: userError } = await supabase.auth.getUser()

  if (userError || !user) {
    throw new Error('No active session. Please log in again.')
  }

  const { data, error } = await supabase.functions.invoke<{ questions: QuizQuestion[] }>('generate-quiz', {
    body: { positionTitle, techStack, difficulty, questionCount, categories },
  })

  if (error) {
    const msg = (error as { message?: string }).message || 'Failed to generate quiz'
    throw new Error(msg)
  }

  if (!data?.questions) {
    throw new Error('No questions returned from AI')
  }

  return data.questions
}

export async function saveQuizSession(
  userId: string,
  applicationId: string | null,
  difficulty: QuizDifficulty,
  categories: QuizCategory[],
  questions: QuizQuestion[],
  correctAnswers: number,
  score: number
): Promise<QuizSession> {
  const { data, error } = await supabase
    .from('quiz_sessions')
    .insert({
      user_id: userId,
      application_id: applicationId,
      difficulty,
      categories,
      total_questions: questions.length,
      correct_answers: correctAnswers,
      score,
      questions,
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to save quiz: ${error.message}`)
  }

  return data as QuizSession
}

export async function fetchUserQuizSessions(userId: string): Promise<QuizSession[]> {
  const { data, error } = await supabase
    .from('quiz_sessions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch quizzes: ${error.message}`)
  }

  return (data || []) as QuizSession[]
}

export async function fetchQuizSessionById(sessionId: string, userId: string): Promise<QuizSession | null> {
  const { data, error } = await supabase
    .from('quiz_sessions')
    .select('*')
    .eq('id', sessionId)
    .eq('user_id', userId)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null // not found
    throw new Error(`Failed to fetch quiz: ${error.message}`)
  }

  return data as QuizSession
}

export async function fetchUserQuizCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('quiz_sessions')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)

  if (error) {
    throw new Error(`Failed to fetch quiz count: ${error.message}`)
  }

  return count || 0
}
